import React, { FC, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Skeleton from '@material-ui/lab/Skeleton';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import LockOpenOutlined from '@material-ui/icons/LockOpenOutlined';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { Row } from 'components/UI/Row';
import { Column } from 'components/UI/Column';
import { StickyDiv } from 'components/UI/StickyDiv';
import map from 'lodash/map';
import {
  closeError,
  deleteDelegate,
  fetchDelegates,
  resetProductionUsers,
  getUsers,
  getUsersLoaders,
  getUsersApiErrors
} from './users';
import { UserForm } from './UserForm';
import { UserType } from './userFormSchema';

const companyAliasColor = '#F38621';
const supervisorColor = '#B821F3';
const loadingColor = '#E0E0E0';

const UsersList: FC<{}> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const users = useSelector(getUsers);
  const { isLoading, isDeletingItems } = useSelector(getUsersLoaders);
  const { loadingError, deletingError } = useSelector(getUsersApiErrors);
  const [isOpenSiteForm, setIsOpenSiteForm] = useState(false);
  const [userToEdit, setUserToEdit] = useState();
  const [updatePassword, setUpdatePassword] = useState(false);

  useEffect(() => {
    dispatch(fetchDelegates(null));
    return () => {
      dispatch(resetProductionUsers());
    };
  }, []);

  const getRole = useCallback((role: string): string => {
    switch (role) {
      case 'company_alias':
        return t('delegates.roles.manager');
      case 'supervisor':
        return t('delegates.roles.worker');
      default:
        return t('delegates.roles.common');
    }
  }, []);

  const getBackground = useCallback(user => {
    if (!user) {
      return loadingColor;
    }
    return user.role === UserType.Supervisor ? supervisorColor : companyAliasColor;
  }, []);

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  // TODO: FOR THIS KIND OF EMPTY LIST MAKE HOOK
  if (!isLoading && isEmpty(users)) {
    return (
      <Column justifyContent="center" alignItems="center">
        <Typography style={{ marginBottom: '25px' }}>{t('delegates.users not found')}</Typography>
        <Button variant="contained" color="primary" onClick={() => setIsOpenSiteForm(true)}>
          {t('delegates.new user')}
        </Button>
        <UserForm isOpen={isOpenSiteForm} closeFn={() => setIsOpenSiteForm(false)} />
      </Column>
    );
  }

  return (
    <>
      <StickyDiv>
        <div style={{ padding: '0 40px' }}>
          <Button variant="contained" color="primary" component="span" onClick={() => setIsOpenSiteForm(true)}>
            {t('delegates.new user')}
          </Button>
        </div>
      </StickyDiv>
      <div style={{ height: '20px' }} />
      <Grid container spacing={2}>
        {map(isLoading ? Array.from(new Array(3)) : users, (user: any, index: number) => (
          <Grid key={`user_${index}`} item xs={12} sm={6} md={4} lg={4} xl={2}>
            <Card variant="outlined">
              <CardHeader
                title={user ? getRole(user.role) : <Skeleton variant="text" animation="wave" width="100%" />}
                style={{
                  background: user ? getBackground(user) : 'transparent',
                  color: 'white',
                  padding: '10px'
                }}
              />
              <CardContent style={{ height: '150px' }}>
                <Row style={{ padding: '10px 0' }}>
                  {user ? (
                    <>
                      {t('delegates.full name')}: {user.first_name} {user.last_name}
                    </>
                  ) : (
                    <Skeleton variant="text" animation="wave" width="100%" />
                  )}
                </Row>
                <Row style={{ padding: '10px 0' }}>
                  {user ? (
                    <>
                      {t('label.email')}: {user.email}
                    </>
                  ) : (
                    <Skeleton variant="text" animation="wave" width="100%" />
                  )}
                </Row>
                <div style={{ flex: 1 }} />
              </CardContent>

              <CardActions style={{ justifyContent: 'space-between' }}>
                <>
                  {user ? (
                    <>
                      <IconButton
                        aria-label="delete"
                        color="secondary"
                        disabled={isDeletingItems && isDeletingItems[user.id]}
                        onClick={() => {
                          dispatch(deleteDelegate(user.id));
                        }}
                      >
                        <DeleteIcon />
                        {isDeletingItems && isDeletingItems[user.id] && (
                          <CircularProgress
                            color="secondary"
                            size={34}
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginTop: -17,
                              marginLeft: -17
                            }}
                          />
                        )}
                      </IconButton>
                      <div>
                        <IconButton
                          aria-label="edit"
                          color="primary"
                          onClick={() => {
                            setUserToEdit(user);
                            setUpdatePassword(true);
                            setIsOpenSiteForm(true);
                          }}
                        >
                          <LockOpenOutlined />
                        </IconButton>
                        <IconButton
                          aria-label="edit"
                          color="primary"
                          onClick={() => {
                            setUserToEdit(user);
                            setIsOpenSiteForm(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </div>
                    </>
                  ) : (
                    <>
                      <Skeleton
                        animation="wave"
                        variant="circle"
                        width={20}
                        height={20}
                        style={{ marginLeft: '15px' }}
                      />
                      <div style={{ display: 'flex' }}>
                        <Skeleton
                          animation="wave"
                          variant="circle"
                          width={20}
                          height={20}
                          style={{ marginRight: '15px' }}
                        />
                        <Skeleton
                          animation="wave"
                          variant="circle"
                          width={20}
                          height={20}
                          style={{ marginRight: '15px' }}
                        />
                      </div>
                    </>
                  )}
                </>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      <UserForm
        isOpen={isOpenSiteForm}
        closeFn={() => {
          setIsOpenSiteForm(false);
          setTimeout(() => {
            setUserToEdit(undefined);
            setUpdatePassword(false);
          });
        }}
        updatePassword={updatePassword}
        user={userToEdit}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(deletingError)}
        autoHideDuration={5000}
        onClose={() => {
          dispatch(closeError());
        }}
      >
        <Alert severity="error" variant="filled" onClose={() => dispatch(closeError())}>
          {deletingError}
        </Alert>
      </Snackbar>
    </>
  );
};

export { UsersList };
